package de.oevbs.kunde.kundenportal2.services.registration

import de.oevbs.kosmos.ui.elements.button.btn
import de.oevbs.kosmos.ui.forms.inputgroup.inputText
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kunde.kundenportal2.Formats
import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.RegistrationStep
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.model.services.registration.*
import de.oevbs.kunde.kundenportal2.services.registration.store.RegistrationMailStore
import de.oevbs.kunde.kundenportal2.services.registration.store.RegistrationUserStore
import de.oevbs.kunde.kundenportal2.theRouter
import dev.fritz2.core.RenderContext
import dev.fritz2.core.invoke
import dev.fritz2.headless.validation.Severity
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine

fun RenderContext.registrationStart() {
    GlobalServiceMessagesStore.forceClear()
    val mailStore = RegistrationMailStore(Job())
    regStep1(mailStore)
}

fun RenderContext.registrationContinue() {
    GlobalServiceMessagesStore.clear()
    val userStore = RegistrationUserStore(Job())
    regStep3(userStore)
}

private fun RenderContext.regStep1(mailStore: RegistrationMailStore) {

    headerImageLayout {
        title("Privatkunden-Account erstellen")
        content {

            div("sm:w-full xl:w-3/4") { // Angaben werden nur für die schmalen Seiten vor dem Login genutzt
                contentContainer {

                    mailStore.currentStep.render { step ->
                        if (step == RegistrationStep.MAILSUCCESS2) {
                            messagePageText(
                                header = "Verifizierungs-Mail wurde versendet.",
                                text = "Es kann bis zu 2 Stunden dauern, bis Sie die Mail erhalten (schauen Sie auch in Ihren Spam-Ordner)."
                                        + " Bitte klicken Sie in der Mail auf den Link, um Ihre Mail-Adresse zu bestätigen. Sie werden"
                                        + " dann zum Kundenportal weitergeleitet, wo Sie die Registrierung abschließen können."
                                        + " Sie können dieses Fenster jetzt schließen.",
                                showRedirectNotice = false
                            )
                        } else {


                            div("my-5") {
                                progressStepper<RegistrationStep> {
                                    activeStep = mailStore.currentStep
                                    steps = RegistrationStep.visible
                                }
                            }

                            paragraph {
                                +"Für die Registrierung bei "
                                b { +"Meine Öffentliche" }
                                +" benötigen wir Ihre Mail-Adresse. Bitte beachten Sie, dass unser Kundenportal derzeit nur von Privatkunden genutzt werden kann."
                            }

                            inputText {
                                value(mailStore.map(RegistrationMail.mail() + Formats.trimString))
                                label("Mail")
                            }

                            inputText {
                                value(mailStore.map(RegistrationMail.mailRepeat() + Formats.trimString))
                                label("Mail wiederholen")
                            }

                            checkboxSingle {
                                value(mailStore.map(RegistrationMail.privacyTermsAccepted()))
                                label {
                                    span {
                                        +"Ich akzeptiere die "
                                        inlineLink(
                                            text = "Nutzungsbedingungen",
                                            link = "https://www.oeffentliche.de/content/oeffentliche/gesetzliche-regelungen/nutzungshinweise/index.html",
                                        )
                                        +" und "
                                        inlineLink(
                                            text = "Datenschutzbestimmungen",
                                            link = "https://www.oeffentliche.de/content/oeffentliche/gesetzliche-regelungen/datenschutz/index.html",
                                        )
                                        +" von "
                                        b { +"Meine Öffentliche" }
                                        +"."
                                    }
                                }
                            }

                            buttonContainer {
                                btn {
                                    label("Weiter")
                                    busy(globalSubmitTracker.data)
                                }.clicks handledBy mailStore.handleMailAddressInput
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun RenderContext.regStep3(userStore: RegistrationUserStore) {

    userStore.currentStep.combine(theRouter.data, ::Pair).render { (step, paramsMap) ->

        headerImageLayout {
            title("Privatkunden-Registrierung abschließen")
            content {

                div("sm:w-full xl:w-3/4") { // Angaben werden nur für die schmalen Seiten vor dem Login genutzt
                    contentContainer {

                        if (step == RegistrationStep.SUCCESS4) {
                            messagePageText(
                                header = "Registrierung abgeschlossen.",
                                text = "Sie können sich nun in das Kundenportal einloggen."
                                        + " Dort können Sie unter anderem Ihre Versicherungsverträge verwalten,"
                                        + " Ihre persönlichen Daten ändern oder einen Schaden melden.",
                                buttonLabel = "Weiter zum Login",
                                pageTarget = Pages.root
                            )
                            return@contentContainer
                        }

                        // Token wurde als URL-Param übergeben: ?token=yyy
                        val tokenParam = paramsMap["token"]
                        val invalidParam = paramsMap["invalid"]
                        if (invalidParam != null || tokenParam == null) { // Link ist ungültig - Fehler anzeigen und zu Step1 weiterleiten
                            messagePageText(
                                header = "Link abgelaufen",
                                text = "Der aufgerufene Bestätigungs-Link ist abgelaufen oder wurde schon verwendet."
                                        + " Bitte starten Sie die Registrierung erneut.",
                                severity = Severity.Error,
                                buttonLabel = "Zur Registrierung",
                                pageTarget = Pages.registration
                            )
                            return@contentContainer
                        }

                        userStore.map(RegistrationUser.token() + Formats.trimString).update(tokenParam)

                        div("my-5") {
                            progressStepper<RegistrationStep> {
                                activeStep = userStore.currentStep
                                steps = RegistrationStep.visible
                            }
                        }

                        paragraph {
                            +"Um das Kundenportal "
                            b { +"Meine Öffentliche" }
                            +" nutzen zu können, müssen Sie einen Benutzernamen und das dazugehörige Passwort festlegen."
                            +" Klicken Sie auf die Fragezeichen an den Feldern, um Hilfe beim Ausfüllen zu erhalten."
                        }

                        inputText {
                            value(userStore.map(RegistrationUser.username() + Formats.lowercase))
                            label("Benutzername")
                            iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.username)
                        }

                        inputPassword {
                            value(userStore.map(RegistrationUser.password() + Formats.trimString))
                            label("Passwort")
                            iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.password)
                        }

                        inputPassword {
                            value(userStore.map(RegistrationUser.passwordRepeat() + Formats.trimString))
                            label("Passwort wiederholen")
                        }

                        paragraph {
                            +"Im Anschluss an die Registrierung senden wir Ihnen eine Mail, die Ihren gewählten Benutzernamen enthält. "
                            +"Sie benötigen diesen für den Login."
                        }

                        buttonContainer {
                            btn {
                                label("Registrierung abschließen")
                                busy(globalSubmitTracker.data)
                            }.clicks handledBy userStore.handleUserData
                        }
                    }
                }
            }
        }
    }
}